/**
 * Admin Panel — Platform Operator Keys API
 *
 * Zod schemas for the cross-tenant credential (`nxs_o_`) described in NEX-3037:
 * one key that acts on ANY organization named in the `organization-id` header,
 * including organizations its owning user has no OrganizationMembership in.
 *
 * Minting is ROOT-only. Every *use* of a minted key is written to
 * `NxAdminAuditLog` by `CrossOrgKeyUseAudit` — the audit is what makes the
 * capability defensible, so it is not optional and not org-opt-in.
 *
 * The raw key is returned EXACTLY ONCE, by Mint. Nothing else can read it back:
 * only the SHA-256 hash and a masked hint are retrievable afterwards.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ============================================================
// Schemas
// ============================================================

/**
 * Hard ceiling on a platform-operator key's lifetime, in days.
 *
 * Mirrors `PLATFORM_OPERATOR_TOKEN_MAX_LIFETIME_DAYS` in the backend service and
 * the `expiresAt IS NOT NULL` half of the DB CHECK constraint. Three layers say
 * the same thing on purpose: the contract rejects it early with a good message,
 * the service rejects a non-HTTP caller, and the database rejects everything
 * else.
 */
export const PLATFORM_OPERATOR_KEY_MAX_LIFETIME_DAYS = 90;

/** One platform-operator key, as listed. Never includes the raw key. */
export const PlatformOperatorKeySchema = z.object({
  id: z.string(),
  /** Masked display value, e.g. "nxs_o_...a3f4". */
  keyHint: z.string(),
  label: z.string().nullable(),
  /** The human this key belongs to. */
  userId: z.string(),
  userEmail: z.string().nullable(),
  scopes: z.array(z.string()),
  /** Always set for this key kind — enforced by CHECK constraint. */
  expiresAt: z.string(),
  lastUsedAt: z.string().nullable(),
  createdAt: z.string(),
  revokedAt: z.string().nullable()
});

export const ListPlatformOperatorKeysResponseSchema = z.object({
  keys: z.array(PlatformOperatorKeySchema)
});

export const MintPlatformOperatorKeyBodySchema = z.object({
  /** Platform user the key is issued to. Keys are personal, never shared. */
  userId: z.string().min(1),
  /** Free-text so an auditor can tell two of nab's keys apart. */
  label: z.string().trim().min(1).max(120),
  /**
   * Granted scopes. `["*:*"]` is permitted but must be asked for explicitly —
   * a read-only operator key is expressible and is the better default.
   */
  scopes: z.array(z.string().min(1)).min(1),
  /**
   * Days until expiry. Mandatory where it is optional for the other two key
   * kinds: an unbounded lifetime on a cross-tenant credential is the whole risk
   * of the feature.
   */
  expiresInDays: z.number().int().positive().max(PLATFORM_OPERATOR_KEY_MAX_LIFETIME_DAYS)
});

export const MintPlatformOperatorKeyResponseSchema = z.object({
  /** The raw `nxs_o_…` key. Shown once, never retrievable again. */
  key: z.string(),
  expiresAt: z.string()
});

export const RevokePlatformOperatorKeyPathVarsSchema = z.object({
  keyId: z.string().min(1)
});

export const RevokePlatformOperatorKeyResponseSchema = z.object({
  id: z.string(),
  revokedAt: z.string()
});

// ============================================================
// ZAdminPlatformOperatorKeys — Endpoint Definitions
// ============================================================

export const ZAdminPlatformOperatorKeys = {
  /**
   * List every platform-operator key, active and revoked.
   * GET /api/admin/platform-operator-keys
   */
  ListPlatformOperatorKeys: {
    method: "GET" as const,
    path: "/api/admin/platform-operator-keys",
    Response: ApiSuccessSchema(ListPlatformOperatorKeysResponseSchema)
  },

  /**
   * Mint a new cross-tenant key. ROOT only.
   * POST /api/admin/platform-operator-keys
   */
  MintPlatformOperatorKey: {
    method: "POST" as const,
    path: "/api/admin/platform-operator-keys",
    Body: MintPlatformOperatorKeyBodySchema,
    Response: ApiSuccessSchema(MintPlatformOperatorKeyResponseSchema)
  },

  /**
   * Revoke a key immediately.
   * POST /api/admin/platform-operator-keys/:keyId/revoke
   */
  RevokePlatformOperatorKey: {
    method: "POST" as const,
    path: "/api/admin/platform-operator-keys/:keyId/revoke",
    PathVars: RevokePlatformOperatorKeyPathVarsSchema,
    Response: ApiSuccessSchema(RevokePlatformOperatorKeyResponseSchema)
  }
} as const;

// ============================================================
// Convenience Type Exports
// ============================================================

export type PlatformOperatorKey = z.infer<typeof PlatformOperatorKeySchema>;
export type ListPlatformOperatorKeysResponse = z.infer<
  typeof ListPlatformOperatorKeysResponseSchema
>;
export type MintPlatformOperatorKeyBody = z.infer<typeof MintPlatformOperatorKeyBodySchema>;
export type MintPlatformOperatorKeyResponse = z.infer<typeof MintPlatformOperatorKeyResponseSchema>;
